import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { startDemoSession } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
    ArrowRight,
    ShieldCheck,
    FileSearch,
    Network,
    Gauge,
    ClipboardCheck,
    AlertTriangle,
    FileText,
    ChevronRight,
    Loader2,
} from "lucide-react";

const Stat = ({ value, label, testid }) => (
    <div
        className="p-6 border-r border-b border-hair last:border-r-0"
        data-testid={testid}
    >
        <div className="heading text-5xl md:text-6xl font-medium tracking-tighter leading-none">
            {value}
        </div>
        <div className="overline mt-3">{label}</div>
    </div>
);

const Feature = ({ icon: Icon, title, body, num }) => (
    <div className="p-8 border-r border-b border-hair last:border-r-0 hover:bg-white transition-colors duration-200">
        <div className="flex items-start justify-between mb-6">
            <div className="w-10 h-10 border border-hair rounded-sm flex items-center justify-center">
                <Icon size={18} strokeWidth={1.75} />
            </div>
            <span className="mono text-[11px] text-neutral-400">{num}</span>
        </div>
        <h3 className="heading text-xl font-medium mb-2 tracking-tight">{title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed">{body}</p>
    </div>
);

export default function Landing() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [launching, setLaunching] = useState(false);

    const launchDemo = async () => {
        if (launching) return;
        setLaunching(true);
        const t = toast.loading("Launching read-only demo…");
        try {
            await startDemoSession();
            await refresh();
            // Every public demo launch is a fresh sales pitch — force the
            // guided tour to auto-start even for return visitors.
            try {
                localStorage.removeItem("audri_tour_seen_v1");
            } catch (_e) {
                /* ignore */
            }
            toast.success(
                "You're in the read-only demo — uploads, audit runs and edits are disabled.",
                { id: t, duration: 6500 },
            );
            navigate("/dashboard");
        } catch (e) {
            const msg =
                e?.response?.status === 429
                    ? "Demo rate limit reached — please try again in a few minutes."
                    : e?.response?.data?.detail ||
                      "Couldn't launch the demo. Please contact sales@audri.app.";
            toast.error(msg, { id: t });
        } finally {
            setLaunching(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fafafa]">
            {/* Nav */}
            <nav className="border-b border-hair bg-white sticky top-0 z-40">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <Link
                        to="/"
                        className="flex items-center gap-2"
                        data-testid="nav-brand"
                    >
                        <img
                            src="/audri-logo.png"
                            alt="AudRI"
                            className="w-9 h-9 rounded-sm"
                        />
                        <div>
                            <div className="heading font-bold leading-none tracking-tight">
                                audri
                            </div>
                            <div className="overline text-[9px] mt-[2px]">
                                Audit Realtime Intelligence
                            </div>
                        </div>
                    </Link>
                    <div className="hidden md:flex items-center gap-8 text-sm">
                        <a className="link-underline" href="#features">
                            Features
                        </a>
                        <a className="link-underline" href="#how">
                            How it works
                        </a>
                        <a className="link-underline" href="#roi">
                            ROI
                        </a>
                        <a className="link-underline" href="#compliance">
                            Compliance
                        </a>
                    </div>
                    <button
                        type="button"
                        onClick={launchDemo}
                        disabled={launching}
                        data-testid="nav-launch-app"
                        className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#9A3412] transition-colors disabled:opacity-60"
                    >
                        {launching ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <ArrowRight size={14} />
                        )}
                        Launch live demo
                    </button>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative border-b border-hair overflow-hidden">
                <div className="absolute inset-0 bg-dots opacity-40 pointer-events-none" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-36 relative">
                    <div className="grid md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8">
                            <div className="overline mb-6 flex items-center gap-3 flex-wrap">
                                <span>AudRI for GxP</span>
                                <span className="text-neutral-300">/</span>
                                <span className="text-neutral-400 normal-case tracking-normal">
                                    a vertical of the AudRI compliance platform
                                </span>
                            </div>
                            <h1 className="heading text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter font-medium">
                                GxP audits,
                                <br />
                                <span className="text-[#C2410C]">
                                    reduced 85–90%.
                                </span>
                            </h1>
                            <p className="mt-8 text-lg text-neutral-600 max-w-xl leading-relaxed">
                                AudRI automates Computer System Validation periodic
                                reviews for life sciences. Ingest SOPs and validation
                                packages. Extract rules. Match evidence. Generate
                                inspection-ready findings — in hours, not weeks.
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <button
                                    type="button"
                                    onClick={launchDemo}
                                    disabled={launching}
                                    data-testid="hero-cta-primary"
                                    className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-6 py-3 rounded-sm hover:bg-[#9A3412] transition-colors disabled:opacity-60"
                                >
                                    {launching ? (
                                        <Loader2
                                            size={14}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <ArrowRight size={14} />
                                    )}
                                    Launch live demo
                                </button>
                                <a
                                    href="#how"
                                    data-testid="hero-cta-secondary"
                                    className="inline-flex items-center gap-2 border border-[#0a0a0a] text-sm px-6 py-3 rounded-sm hover:bg-[#0a0a0a] hover:text-white transition-colors"
                                >
                                    See how it works <ChevronRight size={14} />
                                </a>
                            </div>
                            <div className="mt-5 text-xs text-neutral-500 leading-relaxed max-w-md">
                                Demo is read-only — browse audits, findings and the
                                tour without signing up. Ready for a pilot?{" "}
                                <a
                                    href="https://calendly.com/keshav-glifiq/glif-demo"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#C2410C] hover:underline"
                                >
                                    Book a 30-min walkthrough
                                </a>
                                .
                            </div>
                            <div className="mt-10 mono text-[11px] text-neutral-500 tracking-widest uppercase">
                                21 CFR Part 11 · GAMP 5 · Annex 11 · ALCOA+
                            </div>
                        </div>

                        <div className="md:col-span-4">
                            <div className="border border-hair bg-white">
                                <div className="p-4 border-b border-hair flex items-center justify-between">
                                    <span className="overline">
                                        Periodic Review · LIMS-PROD-01
                                    </span>
                                    <span className="mono text-[11px] text-[#C2410C]">
                                        LIVE
                                    </span>
                                </div>
                                <div className="p-6 space-y-4">
                                    <div>
                                        <div className="overline mb-1">Compliance</div>
                                        <div className="num text-5xl font-medium tracking-tight">
                                            89.4%
                                        </div>
                                    </div>
                                    <div className="h-[1px] bg-neutral-200" />
                                    <div className="grid grid-cols-2 gap-4 text-xs">
                                        <div>
                                            <div className="overline mb-1">
                                                Rules
                                            </div>
                                            <div className="num text-xl">11</div>
                                        </div>
                                        <div>
                                            <div className="overline mb-1">
                                                Findings
                                            </div>
                                            <div className="num text-xl">5</div>
                                        </div>
                                        <div>
                                            <div className="overline mb-1">
                                                Critical
                                            </div>
                                            <div className="num text-xl text-[#991B1B]">
                                                1
                                            </div>
                                        </div>
                                        <div>
                                            <div className="overline mb-1">
                                                Time saved
                                            </div>
                                            <div className="num text-xl text-[#064E3B]">
                                                112h
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats band */}
            <section className="border-b border-hair">
                <div className="max-w-[1440px] mx-auto grid grid-cols-2 md:grid-cols-4 border-l border-hair">
                    <Stat
                        value="120h"
                        label="Manual audit · per system"
                        testid="stat-manual"
                    />
                    <Stat
                        value="4–8h"
                        label="With AudRI · per system"
                        testid="stat-audri"
                    />
                    <Stat
                        value="$14.8M"
                        label="Avg cost of a single GxP consent decree"
                        testid="stat-cost"
                    />
                    <Stat
                        value="100%"
                        label="Traceability · rule → test → evidence"
                        testid="stat-trace"
                    />
                </div>
            </section>

            {/* Features */}
            <section id="features" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
                <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
                    <div className="md:col-span-7">
                        <div className="overline mb-4">01 · Capabilities</div>
                        <h2 className="heading text-4xl md:text-5xl tracking-tight font-medium">
                            A purpose-built audit engine for regulated systems.
                        </h2>
                    </div>
                    <p className="md:col-span-5 text-neutral-600 leading-relaxed">
                        AudRI replaces the most painful, error-prone phases of CSV
                        periodic review: rule extraction, evidence matching, and
                        findings authoring. Every output is inspection-ready and fully
                        traceable.
                    </p>
                </div>
                <div className="border-t border-l border-hair grid md:grid-cols-3">
                    <Feature
                        num="F.01"
                        icon={FileSearch}
                        title="SOP rule extraction"
                        body="Parse unstructured SOPs. Extract atomic, testable compliance rules with section references. Claude Haiku 4.5 in the loop."
                    />
                    <Feature
                        num="F.02"
                        icon={ClipboardCheck}
                        title="Evidence ingestion"
                        body="Upload URS, IQ, OQ, PQ, change controls, deviations. AudRI normalizes and indexes every artifact."
                    />
                    <Feature
                        num="F.03"
                        icon={Network}
                        title="Semantic matching"
                        body="Map rules → requirements → test cases → evidence. PASS / CONDITIONAL / FAIL verdicts with reasoning for every link."
                    />
                    <Feature
                        num="F.04"
                        icon={Gauge}
                        title="Traceability matrix"
                        body="Auto-built, coverage-scored, gap-highlighted. Replace spreadsheet archaeology with a living matrix."
                    />
                    <Feature
                        num="F.05"
                        icon={AlertTriangle}
                        title="Findings & CAPA"
                        body="Findings classified Critical / Major / Minor / Observation with AI-authored corrective and preventive actions."
                    />
                    <Feature
                        num="F.06"
                        icon={FileText}
                        title="Inspection-ready reports"
                        body="Periodic review reports that double as FDA 483 / Annex 11 inspection packs. One click to export."
                    />
                </div>
            </section>

            {/* How */}
            <section id="how" className="border-y border-hair bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
                    <div className="overline mb-4">02 · Workflow</div>
                    <h2 className="heading text-4xl md:text-5xl tracking-tight font-medium mb-16 max-w-3xl">
                        From SOP to signed-off audit in four steps.
                    </h2>
                    <div className="grid md:grid-cols-4 border-t border-l border-hair">
                        {[
                            {
                                n: "01",
                                t: "Register system",
                                d: "Add your GxP system — LIMS, MES, EDMS, SCADA, ERP-GxP. Risk level, owner, department.",
                            },
                            {
                                n: "02",
                                t: "Ingest documents",
                                d: "Drop in SOPs, URS, IQ/OQ/PQ, change controls, deviations. PDF, DOCX, TXT.",
                            },
                            {
                                n: "03",
                                t: "Run AudRI",
                                d: "The engine extracts rules, matches evidence, scores coverage and authors findings.",
                            },
                            {
                                n: "04",
                                t: "Review & export",
                                d: "QA reviews, adjusts severity, approves. Export inspection-ready PDF / push to DMS.",
                            },
                        ].map((s) => (
                            <div
                                key={s.n}
                                className="p-8 border-r border-b border-hair"
                            >
                                <div className="mono text-[11px] text-[#C2410C] mb-6">
                                    STEP {s.n}
                                </div>
                                <div className="heading text-xl font-medium mb-3 tracking-tight">
                                    {s.t}
                                </div>
                                <div className="text-sm text-neutral-600">{s.d}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ROI */}
            <section id="roi" className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="overline mb-4">03 · Return</div>
                        <h2 className="heading text-4xl md:text-5xl tracking-tight font-medium">
                            The math of manual audits is brutal.
                        </h2>
                        <p className="mt-6 text-neutral-600 leading-relaxed">
                            A mid-sized biotech with 40 GxP systems running biennial
                            periodic reviews spends ~3,200 QA hours every two years on
                            paperwork — and still risks 483s from missed gaps.
                        </p>
                    </div>
                    <div className="md:col-span-7 border border-hair bg-white">
                        <div className="grid grid-cols-3 border-b border-hair">
                            <div className="p-6 border-r border-hair">
                                <div className="overline">Method</div>
                            </div>
                            <div className="p-6 border-r border-hair">
                                <div className="overline">Hours / system</div>
                            </div>
                            <div className="p-6">
                                <div className="overline">40 systems / 2 yrs</div>
                            </div>
                        </div>
                        {[
                            ["Fully manual", "120 h", "4,800 h"],
                            ["Spreadsheet-assisted", "72 h", "2,880 h"],
                            ["AudRI", "6 h", "240 h"],
                        ].map(([m, h, t], i) => (
                            <div
                                key={m}
                                className={`grid grid-cols-3 ${
                                    i === 2
                                        ? "bg-[#fff7ed] border-t border-hair"
                                        : "border-b border-hair"
                                }`}
                            >
                                <div className="p-6 border-r border-hair heading font-medium">
                                    {m}
                                </div>
                                <div className="p-6 border-r border-hair num">
                                    {h}
                                </div>
                                <div className="p-6 num">{t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Compliance */}
            <section id="compliance" className="border-y border-hair bg-[#0a0a0a] text-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
                    <div className="grid md:grid-cols-12 gap-10">
                        <div className="md:col-span-5">
                            <div className="overline mb-4 text-neutral-400">
                                04 · Compliance
                            </div>
                            <h2 className="heading text-4xl md:text-5xl tracking-tight font-medium">
                                Built for the way regulators read.
                            </h2>
                        </div>
                        <div className="md:col-span-7 text-neutral-300 leading-relaxed text-lg">
                            Every AudRI output maps to GAMP 5 categories, 21 CFR Part 11
                            audit-trail and e-signature controls, EU GMP Annex 11,
                            ALCOA+ data-integrity principles and your internal QMS SOPs.
                            Human-in-the-loop approval is preserved end-to-end.
                        </div>
                    </div>
                    <div className="grid md:grid-cols-4 mt-16 border-t border-neutral-800">
                        {["21 CFR Part 11", "GAMP 5", "EU Annex 11", "ALCOA+"].map(
                            (t) => (
                                <div
                                    key={t}
                                    className="p-8 border-r border-neutral-800 last:border-r-0"
                                >
                                    <ShieldCheck
                                        size={20}
                                        className="text-[#C2410C] mb-4"
                                    />
                                    <div className="heading text-lg font-medium">
                                        {t}
                                    </div>
                                </div>
                            ),
                        )}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-24">
                <div className="border border-hair bg-white p-12 md:p-20 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
                    <div>
                        <div className="overline mb-4">Ready to audit faster</div>
                        <h2 className="heading text-4xl md:text-6xl tracking-tighter font-medium">
                            Run a demo audit
                            <br />
                            in 90 seconds.
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={launchDemo}
                        disabled={launching}
                        data-testid="cta-bottom-launch"
                        className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-8 py-4 rounded-sm hover:bg-[#9A3412] transition-colors disabled:opacity-60"
                    >
                        {launching ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <ArrowRight size={14} />
                        )}
                        Launch live demo
                    </button>
                </div>
            </section>

            <footer className="border-t border-hair">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-8 flex flex-wrap justify-between gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <img
                            src="/audri-logo.png"
                            alt="AudRI"
                            className="w-6 h-6 rounded-sm"
                        />
                        <span className="heading font-medium text-sm">audri</span>
                        <span className="mono text-[11px] text-neutral-500 ml-4">
                            © {new Date().getFullYear()} Audit Realtime Intelligence
                        </span>
                    </div>
                    <div className="flex items-center gap-6 mono text-[11px] text-neutral-500 uppercase tracking-widest">
                        <Link
                            to="/legal/privacy"
                            data-testid="footer-privacy"
                            className="hover:text-[#C2410C]"
                        >
                            Privacy
                        </Link>
                        <Link
                            to="/legal/terms"
                            data-testid="footer-terms"
                            className="hover:text-[#C2410C]"
                        >
                            Terms
                        </Link>
                        <Link
                            to="/legal/dpa"
                            data-testid="footer-dpa"
                            className="hover:text-[#C2410C]"
                        >
                            DPA
                        </Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
